import { AxiosInstance } from 'axios'
import { useAdminStore, useCopilotStore } from '@getmayday/vue-modules'
import { useAgentStore } from '@getmayday/vue-modules/agent'

type HelpdeskStore = {
  adminClient: AxiosInstance | null
  client: any | null
  authLoading: boolean
  initClient: () => Promise<any>
  login: () => Promise<any>
  injectAnswer: (answer: string) => Promise<any>
  dispatchAction: (action: any) => Promise<any>
  getRecommendations: () => Promise<any>
}

export const useIadvizeStore = (store: HelpdeskStore, adminClient: AxiosInstance) => {
  // state
  store.adminClient = adminClient

  // actions
  store.initClient = async () => {
    if (window.location.hostname === 'localhost') return
    try {
      // @ts-ignore
      const client = await window.idzCpa.init()
      if (client) {
        store.client = client
        console.log('iAdvize initialized')
        return client
      } else throw new Error('Error while initializing iAdvize')
    } catch (e) {
      console.error('Error while initializing iAdvize', e)
      return null
    }
  }

  store.login = async () => {
    if (!store.adminClient) return
    store.authLoading = true
    const adminStore = useAdminStore()
    const projectId = store.client?.context.projectId
    try {
      const { data } = await store.adminClient.post(`/login/iadvize/${projectId}`, null, {
        headers: {
          'x-iadvize-token': await store.client?.getJWT()
        }
      })
      const { accessToken, refreshToken, accessExpirationDate } = data
      const configStore = useConfigStore()
      await adminStore.initSessionFromToken({
        accessToken,
        refreshToken,
        accessExpirationDate
      })
      await configStore.initAppFromLogin()
      store.authLoading = false
      return data
    } catch (e) {
      console.error('Error while logging in iAdvize', e)
      store.authLoading = false
      return null
    }
  }

  store.getRecommendations = async (forceCache?: boolean) => {
    const adminStore = useAdminStore()
    const configStore = useConfigStore()
    const copilotStore = useCopilotStore()
    const agentStore = useAgentStore()

    const { companyHasIntegration, companyHasPreference, hasCompanyBrainAgentsPreference } =
      adminStore
    const hasIntegration = companyHasIntegration('iadvize')
    if (!hasIntegration) return
    const mergeRecommendations = companyHasPreference('WEB_EXT_MERGE_RECOMMENDATIONS')
    const hasMultiSalesforcePreference = companyHasPreference('HAS_MULTI_SALESFORCE_ACCOUNT')

    const conversationId = store.client?.context?.conversationId
    if (!conversationId) return
    const ticketContext = {
      tabSource: 'iadvize',
      context: 'iadvize',
      conversationId
    }
    configStore.setCurrentTabContext(ticketContext)
    copilotStore.reset()

    if (hasCompanyBrainAgentsPreference) {
      agentStore.newSession()
      if (!agentStore.currentAgentCopilotTool) {
        agentStore.setCurrentAgent('mayday:copilot')
        if (!agentStore.currentAgent?.id) {
          const agentsWithCopilot = agentStore.getAgentsWithTool('copilot')
          if (agentsWithCopilot?.length) agentStore.setCurrentAgent(agentsWithCopilot[0].id)
        }
      }
    }
    await copilotStore.getNewCopilotRecommendations({
      ...ticketContext,
      preferredLanguage: configStore.locale,
      hasIntegration,
      mergeRecommendations,
      hasMultiSalesforcePreference,
      forceCache: forceCache || configStore.geniusTestMode,
      agentId: agentStore.currentAgent?.id
    })
  }

  store.injectAnswer = async (answer: string) => {
    store.client?.insertTextInComposeBox(answer)
  }

  return store
}
